import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { useCart } from "../App";
import { ShoppingCart, User, Menu, X, Search, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import axios from "axios";
import { API } from "../App";

// New official Brendolly's Auto Parts logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_auto-parts-shop-32/artifacts/j4kogefz_download.png";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Shop", path: "/shop" },
    { name: "VIN Search", path: "/vin-search" },
    { name: "About", path: "/about" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  const categories = [
    { name: "Brakes", slug: "brakes" },
    { name: "Suspension", slug: "suspension" },
    { name: "Ignition", slug: "ignition" },
    { name: "Lighting", slug: "lighting" },
    { name: "Multimedia", slug: "multimedia" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const phone = siteSettings?.phone || "781-870-7976";

  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-200 shadow-sm" data-testid="navbar">
      {/* Top bar */}
      <div className="bg-primary py-1.5">
        <div className="container mx-auto px-4 flex items-center justify-between text-xs text-white">
          <span className="font-medium">Free Shipping on Orders Over $100</span>
          <span className="hidden md:block">Need Help? Call {phone}</span>
        </div>
      </div>

      {/* Main navbar */}
      <nav className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Large and Prominent */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Brendolly's Auto Parts" 
              className="h-20 sm:h-24 md:h-28 lg:h-32 w-auto object-contain"
              style={{ maxWidth: '280px' }}
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 ml-8">
            {navLinks.map((link) => (
              link.name === "Shop" ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger className={`nav-link flex items-center gap-1 text-sm font-medium ${location.pathname.startsWith('/shop') ? 'active' : ''}`} data-testid="shop-dropdown">
                    Shop <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white border-gray-200">
                    <DropdownMenuItem asChild>
                      <Link to="/shop" className="cursor-pointer" data-testid="all-products-link">All Products</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-200" />
                    {categories.map((cat) => (
                      <DropdownMenuItem key={cat.slug} asChild>
                        <Link to={`/shop/${cat.slug}`} className="cursor-pointer" data-testid={`category-${cat.slug}-link`}>
                          {cat.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`nav-link text-sm font-medium ${location.pathname === link.path ? 'active' : ''}`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Right side actions - Smaller and Secondary */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-500 hover:text-gray-700 h-9 w-9"
              onClick={() => navigate("/shop")}
              data-testid="search-btn"
            >
              <Search className="w-4 h-4" />
            </Button>

            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              className="relative text-gray-500 hover:text-gray-700 h-9 w-9"
              onClick={() => navigate("/cart")}
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-4 h-4" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="cart-count">
                  {itemCount}
                </span>
              )}
            </Button>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-700 h-9 w-9" data-testid="user-menu-btn">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border-gray-200" align="end">
                  <div className="px-3 py-2 text-sm">
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-gray-500 text-xs">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer" data-testid="profile-link">My Profile</Link>
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer" data-testid="admin-dashboard-link">Admin Dashboard</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/blog" className="cursor-pointer" data-testid="admin-blog-link">Manage Blog</Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-secondary" data-testid="logout-btn">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/auth")}
                className="text-gray-500 hover:text-gray-700 h-9 px-2 sm:px-3"
                data-testid="login-btn"
              >
                <User className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline text-sm">Login</span>
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-gray-600 hover:text-gray-900 h-9 w-9"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-gray-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-gray-700 hover:text-primary font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Categories</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/shop/${cat.slug}`}
                    className="block text-gray-600 hover:text-primary py-2"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-category-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
